import { useState } from 'react';
import { PaymentMethod } from 'vopay';
import { useTranslation } from 'react-i18next';
import usePortalContacts from './usePortalContacts';
import { useClientData } from '@/providers/ClientData.provider';
import { useAlertDialog } from '@/providers/AlertDialog.provider';

export type PortalDropdownContact = {
  id: string;
  name: string;
  currency?: string;
  availableFunds?: number;
  paymentRails?: PaymentMethod[];
};

type Options = {
  includeBalance?: boolean;
  includePaymentRails?: boolean;
};

export default function usePortalDropdownContacts({
  includeBalance = false,
  includePaymentRails = false,
}: Options = {}) {
  const {
    response: { clientAccountsEnabled },
  } = useClientData();
  const alertDialog = useAlertDialog();
  const { t } = useTranslation('translations', { keyPrefix: 'Global' });
  const { fetchPortalClientAccounts, fetchPortalContacts } = usePortalContacts();
  const [dropdownContacts, setDropdownContacts] = useState<PortalDropdownContact[]>([]);
  const [fetchingContacts, setFetchingContacts] = useState(false);

  async function getDropdownItems(searchTerm: string) {
    setFetchingContacts(true);
    try {
      if (clientAccountsEnabled) {
        const res = await fetchPortalClientAccounts({
          Limit: 50,
          SearchText: searchTerm || undefined,
          IncludeBalance: includeBalance,
          ...(includePaymentRails && { IncludePaymentRails: true }),
        });
        setDropdownContacts(
          res.ClientAccounts?.map((c) => ({
            id: c.ClientAccountID,
            name: c.ClientDisplayName,
            currency: c.Currency,
            availableFunds: c.AvailableFunds,
            ...(includePaymentRails && { paymentRails: c.PaymentRails }),
          })) || [],
        );
      } else {
        const res = await fetchPortalContacts({
          Limit: 50,
          SearchText: searchTerm || undefined,
          ...(includePaymentRails && { IncludePaymentRails: true }),
        });
        setDropdownContacts(
          res.Contacts?.map((c) => ({
            id: c.ContactID,
            name: c.ContactName,
            ...(includePaymentRails && { paymentRails: c.PaymentRails }),
          })) || [],
        );
      }
    } catch (err) {
      console.error(err);
      alertDialog.error(t('error', { defaultValue: 'Error' }), t('genericError', { defaultValue: 'Something went wrong' }), {
        abortLabel: t('Buttons.dismiss', { defaultValue: 'Dismiss' }),
      });
    } finally {
      setFetchingContacts(false);
    }
  }

  return { dropdownContacts, getDropdownItems, fetchingContacts };
}
